// Price history chart (Lane 1335, plan §0.7): grey = cost, blue = sold to
// others, orange = sold to this account (same dot size), a thin trend line
// per series, a dashed "our cost" line. Every point comes from the server:
// purchases (with the server's own outlier flag, shown hollow, never hidden),
// or, for a part never bought (XMEK kits), the monthly invoice cost the part
// page plots. No client-side rule hides or counts points.
import React from 'react';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { money, shortDate } from './quotingText';

const SELL = '#2251ff';
const BUY = '#9ca3af';
const CUST = '#e8590c';

const toTime = (d) => new Date(`${String(d).slice(0, 10)}T00:00:00`).getTime();
const monthTick = (t) => { const d = new Date(t); return `${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`; };

// Even date marks (Ryan, 2026-09-27: the automatic ticks skipped months at
// random). Over two months: the smallest step of 1, 2, 3 or 6 months, or 1, 2,
// 5 or 10 years, that gives at most 10 marks, on calendar boundaries (quarters
// start in Jan/Apr/Jul/Oct, years in January). Two months or less: marks on
// whole days, labelled month/day.
const MONTH_STEPS = [1, 2, 3, 6, 12, 24, 60, 120];
const DAY_MS = 864e5;
export function monthTicks(t0, t1) {
  const a = new Date(t0); const b = new Date(t1);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || t1 < t0) return { ticks: [], days: false };
  if (t1 - t0 <= 62 * DAY_MS) {
    const stepDays = Math.max(1, Math.ceil((t1 - t0) / DAY_MS / 8));
    const d = new Date(a.getFullYear(), a.getMonth(), a.getDate() + 1);
    const ticks = [];
    for (; +d <= t1 && ticks.length < 10; d.setDate(d.getDate() + stepDays)) ticks.push(+d);
    return { ticks, days: true };
  }
  const span = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1;
  const step = MONTH_STEPS.find((s) => span / s <= 10) || MONTH_STEPS[MONTH_STEPS.length - 1];
  const d = new Date(a.getFullYear(), a.getMonth(), 1);
  if (+d < t0) d.setMonth(d.getMonth() + 1);
  const aligned = (x) => (step < 12 ? x.getMonth() % step === 0 : x.getMonth() === 0 && x.getFullYear() % (step / 12) === 0);
  for (let guard = 0; !aligned(d) && guard < 240; guard += 1) d.setMonth(d.getMonth() + 1);
  const ticks = [];
  for (; +d <= t1 && ticks.length < 10; d.setMonth(d.getMonth() + step)) ticks.push(+d);
  return { ticks, days: false };
}

function square(fill, hollow) {
  return function Square({ cx, cy }) {
    if (cx == null || cy == null) return null;
    return <rect x={cx - 4} y={cy - 4} width={8} height={8} fill={hollow ? 'none' : fill} stroke={fill} strokeWidth={1.5} />;
  };
}
function circle(fill) {
  return function Circle({ cx, cy }) {
    if (cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={4} fill={fill} />;
  };
}
const none = () => null;

function Tip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="qtng-tip">
      {shortDate(p.date)} · {p.label}<br />{money(p.y)} per piece
    </div>
  );
}

export default function QuotingChart({ chart, monthlyCost, ourCost, who }) {
  const sold = (chart?.sold || []).filter((s) => s.unit_price != null);
  const bought = (chart?.bought || []).filter((b) => b.unit_price != null && Number(b.unit_price) > 0);
  const costFromSales = !bought.length;
  const cost = costFromSales
    ? (monthlyCost || []).filter((m) => m.cost_unit_price != null)
      .map((m) => ({ x: toTime(`${m.month}-15`), y: Number(m.cost_unit_price), date: `${m.month}-15`,
                     label: 'invoice cost that month' }))
    : bought.map((b) => ({ x: toTime(b.date), y: Number(b.unit_price), date: b.date, outlier: b.is_outlier,
                           label: b.is_outlier ? 'bought (unusual price)' : 'bought' }));
  const mine = sold.filter((s) => s.this_account)
    .map((s) => ({ x: toTime(s.invoice_date || s.date), y: Number(s.unit_price), date: s.invoice_date || s.date,
                   label: `sold to ${who}` }));
  const others = sold.filter((s) => !s.this_account)
    .map((s) => ({ x: toTime(s.invoice_date || s.date), y: Number(s.unit_price), date: s.invoice_date || s.date,
                   label: 'sold to others' }));
  const allSold = [...mine, ...others];
  if (!cost.length && !allSold.length) return null;
  const xs = [...cost, ...allSold].map((p) => p.x);
  const t0 = Math.min(...xs);
  const t1 = Math.max(...xs);
  // A little room either side so the first and last dots are not on the edge.
  const padT = Math.max((t1 - t0) * 0.02, 5 * 864e5);
  const { ticks, days } = monthTicks(t0 - padT, t1 + padT);
  const dayTick = (v) => { const d = new Date(v); return `${d.getMonth() + 1}/${d.getDate()}`; };
  const normalCost = cost.filter((c) => !c.outlier);
  const unusualCost = cost.filter((c) => c.outlier);
  return (
    <div>
      <div className="qtng-lg">
        <span><i className="qtng-sq" style={{ background: BUY }} />{costFromSales ? 'Cost (invoice cost by month)' : 'Cost (what we paid)'}</span>
        <span><i style={{ background: SELL }} />Sold to others</span>
        <span><i style={{ background: CUST }} />Sold to {who}</span>
        {unusualCost.length > 0 && <span className="qtng-mut">Hollow square = a purchase at an unusual price</span>}
        <span className="qtng-mut">Thin lines = trend</span>
      </div>
      <div style={{ width: '100%', height: 230 }}>
        <ResponsiveContainer>
          <ScatterChart margin={{ top: 10, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid stroke="#eef0f3" vertical={false} />
            <XAxis type="number" dataKey="x" domain={[t0 - padT, t1 + padT]} ticks={ticks} interval={0}
                   tickFormatter={days ? dayTick : monthTick} tick={{ fontSize: 11, fill: '#8d919b' }} scale="time" />
            <YAxis type="number" dataKey="y" tickFormatter={(v) => (Number(v) === 0 ? '$0' : money(v))} width={64}
                   tick={{ fontSize: 11, fill: '#8d919b' }} />
            <Tooltip content={<Tip />} cursor={false} />
            {ourCost != null && (
              <ReferenceLine y={Number(ourCost)} stroke={BUY} strokeDasharray="4 4"
                             label={{ value: `our cost ${money(ourCost)}`, position: 'insideTopLeft', fontSize: 11, fill: '#8d919b' }} />
            )}
            {allSold.length > 2 && (
              <Scatter data={allSold} shape={none} line={{ stroke: SELL, strokeWidth: 1.5, strokeOpacity: 0.45 }}
                       lineType="fitting" isAnimationActive={false} legendType="none" />
            )}
            {normalCost.length > 2 && (
              <Scatter data={normalCost} shape={none} line={{ stroke: BUY, strokeWidth: 1.5, strokeOpacity: 0.45 }}
                       lineType="fitting" isAnimationActive={false} legendType="none" />
            )}
            <Scatter data={normalCost} shape={square(BUY, false)} isAnimationActive={false} />
            <Scatter data={unusualCost} shape={square(BUY, true)} isAnimationActive={false} />
            <Scatter data={others} shape={circle(SELL)} isAnimationActive={false} />
            <Scatter data={mine} shape={circle(CUST)} isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
